/**
 * Clean a Draft.js raw content object by removing unreferenced entities from entityMap.
 *
 * Draft.js entityMap can accumulate stale IMAGE entities when users delete images
 * from the editor. convertToRaw() preserves all entities even if no block references them.
 * This function removes unreferenced entities and re-indexes the remaining ones.
 *
 * @param {object} rawContent - Draft.js raw content object (from convertToRaw)
 * @returns {object} - Cleaned raw content object with only referenced entities
 */
export function cleanDraftEntityMap(rawContent) {
  if (!rawContent || !rawContent.blocks || !rawContent.entityMap) {
    return rawContent;
  }

  // Collect all entity keys actually referenced by blocks
  const referencedKeys = new Set();
  rawContent.blocks.forEach(block => {
    if (block.entityRanges) {
      block.entityRanges.forEach(range => {
        referencedKeys.add(String(range.key));
      });
    }
  });

  // Build new entityMap with only referenced entities, re-indexed from 0
  const oldToNew = {};
  const newEntityMap = {};
  let newIndex = 0;

  for (const oldKey of Object.keys(rawContent.entityMap)) {
    if (referencedKeys.has(oldKey)) {
      oldToNew[oldKey] = newIndex;
      newEntityMap[newIndex] = rawContent.entityMap[oldKey];
      newIndex++;
    }
  }

  // Update entityRanges in blocks to use new keys
  const newBlocks = rawContent.blocks.map(block => ({
    ...block,
    entityRanges: (block.entityRanges || []).map(range => ({
      ...range,
      key: oldToNew[String(range.key)]
    })).filter(range => range.key !== undefined)
  }));

  return {
    ...rawContent,
    blocks: newBlocks,
    entityMap: newEntityMap
  };
}

/**
 * Extract all image URLs from a Draft.js raw content object's entityMap.
 *
 * @param {object} rawContent - Draft.js raw content object
 * @returns {string[]} - Array of image source URLs
 */
export function extractImageUrls(rawContent) {
  if (!rawContent || !rawContent.entityMap) return [];

  return Object.values(rawContent.entityMap)
    .filter(entity => entity.type === 'IMAGE' && entity.data?.src)
    .map(entity => entity.data.src);
}
